// Dice genre resolver: maps the `randoms` stream (RT-4) + bet + selection → outcome.
// In PRODUCTION this logic lives in the Go engine of rgs-runtime; here it (a) drives the dev FakePlatformClient
// and (b) re-derives the round outcome CLIENT-SIDE from a settled round's provably-fair reveal, so dice renders
// the roll + win/lose off `SettledRound.fairness` — the package's SettledRound carries money + fairness, not the
// game detail. Same seeds ⇒ same outcome, for both the fake and a real server reveal (09-round-contract §7).

import { deriveRandoms, fromHex, intN } from "@blocklabsdev/rgs-game-manager-frontend-api";
// ENGINE-resolve comes from CONTRACTS (@blocklabsdev/rgs-contracts diceResolve), not a local engine mirror. The
// RGS derivation (deriveRandoms/intN above) STAYS in the SDK.
import { resolve as engineResolve, RANDOMS_PER_STEP, RESOLUTION, type Mode, type Sel } from "./contract";
import type { Fairness } from "@blocklabsdev/rgs-game-manager-frontend-api";

/** The genre (dice) provides this: it maps randoms + bet + selection → outcome. It is the logic that in
 *  production lives in rgs-runtime; here we inject it into the fake AND replay it for the client render. */
export interface GameOutcome {
  result: "win" | "lose" | "void";
  winMinor: number;
  detail?: unknown;
}

export interface GameResolver {
  randomsPerRound: number;
  resolve(randoms: bigint[], betMinor: number, params: unknown): GameOutcome;
}

export interface DiceDetail {
  k: number;
  roll: number;
  mode: Mode;
  /** under/over threshold (hundredths). */
  target?: number;
  /** between/outside half-open band [lo, hi) (hundredths). */
  lo?: number;
  hi?: number;
  betMinor: number;
  winCount: number;
  multiplierPpm: number;
}

export function diceResolver(): GameResolver {
  return {
    randomsPerRound: RANDOMS_PER_STEP,
    resolve(randoms: bigint[], betMinor: number, params: unknown): GameOutcome {
      const sel = params as Sel;
      // Rejection exhaustion (all randoms in the rejection tail, prob p^RANDOMS_PER_STEP): in production the
      // engine returns ErrInsufficientRandoms and the RGS voids the round (VOID). We mirror that here.
      let k: number;
      try {
        ({ value: k } = intN(randoms, RESOLUTION));
      } catch {
        return { result: "void", winMinor: 0 };
      }
      const r = engineResolve(k, BigInt(betMinor), sel);
      const detail: DiceDetail = {
        k,
        roll: k / 100, // presentation (roll = k/100), computed here — contract resolve returns money-math only
        mode: sel.mode,
        ...(sel.target !== undefined ? { target: sel.target } : {}),
        ...(sel.lo !== undefined ? { lo: sel.lo } : {}),
        ...(sel.hi !== undefined ? { hi: sel.hi } : {}),
        betMinor,
        winCount: r.winCount,
        multiplierPpm: r.multiplierPpm,
      };
      return { result: r.win ? "win" : "lose", winMinor: Number(r.winCredits), detail };
    },
  };
}

// outcomeFromFairness re-derives a settled round's outcome (roll + win/lose + detail) CLIENT-SIDE from its
// provably-fair reveal, using the SAME KDF + engine the server/fake used to produce it. dice renders off this
// because the shared SDK's SettledRound carries money + fairness only, not the game detail. Returns undefined
// if the reveal is incomplete (⇒ the caller falls back to a minimal money-only render). Presentation only — the
// authoritative money (payout/balance) always comes from the SettledRound, never from this replay.
export async function outcomeFromFairness(
  f: Fairness,
  sel: Sel,
  betMinor: number,
): Promise<GameOutcome | undefined> {
  if (!f.serverSeed || !f.clientSeed) return undefined;
  const randoms = await deriveRandoms(
    { serverSeed: fromHex(f.serverSeed), publicSeed: f.clientSeed, nonce: f.revealedNonce },
    RANDOMS_PER_STEP,
  );
  return diceResolver().resolve(randoms, betMinor, sel);
}
